// Weekly Quantum Edge — content configuration.
// Runtime URLs come from env vars where possible so they can be updated without a rebuild.
export const WQE = {
  brand: "Weekly Quantum Edge",
  parentBrand: "Quantum Results",
  logoUrl:
    "https://customer-assets-jai6qajn.emergentagent.net/job_paywall-hub-1/artifacts/6sxe778m_logo-transparent-black-font.png",
  creatorName: "Dan Hoover",

  priceLabel: "$200 per month",
  paymentLinkUrl: "https://buy.stripe.com/bJe14nby38eEg34gWwfEk0U",
  meetingLine1: "Every Wednesday",
  meetingLine2: "5:30 – 6:30 p.m. Mountain",
  zoomUrl: "https://us02web.zoom.us/j/3499846893",
  whatsappUrl: "https://chat.whatsapp.com/FGBy0HgPiIzLjxqmZp8omG?mode=gi_t",
  youtubeUrl: "https://www.youtube.com/playlist?list=PLOSYkE1q91_A",
  contactEmail: "dan@quantumresults.com",
  contactPhone: "(248) 808-1093",
  calendarIcsUrl: "/api/weeklyqe/calendar.ics",
  fourLevelsUrl: "/",

  hero: {
    headline: "Continue with Weekly Quantum Edge",
    body:
      "Recognition changes a moment. Practice changes a life.\n\nIf the Four Levels Experience helped you see yourself differently, Weekly Quantum Edge is a place to continue that journey. Each week we come together for thoughtful teaching, meaningful conversation, and practical reflection that helps you apply what you're discovering to everyday life.\n\nThere is no pressure to become someone else. Only an opportunity to reconnect with who you already are.",
    cta: "Continue with Weekly Quantum Edge",
  },
  why: {
    eyebrow: "Why Weekly Quantum Edge exists",
    headline: "A meaningful insight can change the way you see your life. Living from that insight takes practice.",
    body:
      "Most of us return to busy schedules, familiar habits, and daily responsibilities. Without intending to, it's easy to slip back into old ways of thinking and reacting.\n\nWeekly Quantum Edge creates a regular opportunity to pause. To reflect. To reconnect with yourself. To explore how awareness becomes something you naturally live rather than something you occasionally remember.\n\nYou don't need more information. You need a place to consistently return to what you already know.",
  },
  week: {
    eyebrow: "What happens each week",
    headline: "Each gathering is different, because each conversation is shaped by the people who show up.",
    body:
      "Together we explore practical ways to apply these ideas to everyday life through:",
    activities: [
      "Practical teaching",
      "Guided reflection",
      "Meaningful discussion",
      "Real-life examples",
      "Live questions and conversation",
    ],
    closing:
      "There is no expectation that you have everything figured out. Come as you are. Listen. Share if you'd like. Leave with greater clarity than you arrived with.\n\nSome weeks you'll leave with a new perspective. Some weeks you'll simply remember something you already knew. Both are valuable.",
  },
  fit: {
    eyebrow: "Is Weekly Quantum Edge right for you?",
    headline: "Weekly Quantum Edge may be a good fit if you find yourself thinking:",
    lines: [
      "I don't want to lose what I experienced in the Four Levels.",
      "I appreciate thoughtful conversations more than motivational speeches.",
      "I'm interested in understanding myself more deeply.",
      "I value steady progress over quick fixes.",
      "I'd enjoy being part of a community that encourages thoughtful growth.",
    ],
    closing: "If those statements resonate with you, you'll likely feel at home here.",
  },
  faq: {
    eyebrow: "Frequently asked",
    headline: "A few things people often ask.",
    items: [
      { q: "When does my membership begin?", a: "Immediately after you enroll." },
      { q: "Can I join the next session?", a: "Yes. Your membership begins immediately, regardless of when you enroll." },
      { q: "What if I miss a session?", a: "A recording will be emailed after each session and added to the private recording library." },
      { q: "Do I have to participate?", a: "No. You're welcome to participate as much or as little as you'd like." },
      { q: "Can I cancel my membership?", a: "Yes. You may cancel at any time before your next monthly billing date." },
    ],
  },
  details: {
    eyebrow: "Practical details",
    headline: "Everything you need to know before you continue.",
    items: [
      { label: "When", value: "Every Wednesday · 5:30–6:30 p.m. Mountain Time" },
      { label: "Note", value: "We occasionally continue a little longer when the discussion naturally unfolds." },
      { label: "Format", value: "Live online gathering · Recordings available if you can't attend live" },
      { label: "Included", value: "Practical teaching · Reflection · Discussion · Questions & conversation" },
      { label: "Membership", value: "$200 per month · Cancel anytime · No refunds" },
    ],
  },
  continueSection: {
    eyebrow: "Continue",
    headline: "If you're ready to continue exploring what you've already begun, I'd be honored to welcome you.",
    body:
      "Weekly Quantum Edge isn't about becoming someone new. It's about living more consistently from what you've already recognized.",
    cta: "Continue with Weekly Quantum Edge",
  },
  questions: {
    eyebrow: "Questions?",
    headline: "If you'd like to talk before joining, or you're wondering whether this is the right next step, I'd be happy to have a conversation.",
    body: "Sometimes a short conversation is the easiest way to decide.",
    cta: "Schedule a Conversation",
    ctaUrl: "https://calendly.com/hooverdan56/30min",
  },
  closing:
    "Thank you for spending this time with me.\n\nI hope the Four Levels Experience offered you a different way of seeing your life. Whether you continue with Weekly Quantum Edge or simply carry these ideas into your everyday experiences, my hope is the same:\n\nThat you continue becoming more intentional in the way you live.",

  // ── Post-purchase Welcome page ─────────────────────────────────────────
  welcome: {
    headline: "Welcome to Weekly Quantum Edge",
    body:
      "Your membership is confirmed. Thank you for choosing to join Weekly Quantum Edge. I'm genuinely looking forward to having you as part of our community.\n\nWeekly Quantum Edge is a weekly live experience designed to help you create greater clarity, confidence, and intentional progress through practical teaching, meaningful reflection, and thoughtful conversation. Every Wednesday we'll spend one focused hour learning together, exploring ideas, asking questions, and identifying practical actions you can immediately apply to your own life.",
    nextStepsHeading: "Your next steps",
    steps: [
      {
        label: "Check your email",
        body:
          "Your Welcome Email is already on its way. It contains everything you'll need to get started, including your Zoom link, weekly meeting schedule, Add-to-Calendar link, WhatsApp Community invitation, Recording Library, and contact information.\n\nIf you don't receive it within a few minutes, please check your Spam or Promotions folder.",
      },
      {
        label: "Save your seat",
        body:
          "Click the Add to My Calendar link in your Welcome Email. This will automatically add your recurring Weekly Quantum Edge session every Wednesday at 5:30 PM Mountain Time.",
      },
      {
        label: "Join us Wednesday",
        body:
          "Simply click your Zoom link a few minutes before the session begins. There's nothing to prepare. Come exactly as you are — with an open mind and a willingness to explore new possibilities.",
      },
    ],
    expectHeading: "What to expect",
    expectBody:
      "Weekly Quantum Edge is not a traditional online class. Each session begins with a focused lesson designed to introduce a practical idea or principle. From there, the conversation expands as members ask questions, share experiences, and explore how the ideas apply to real life.\n\nThere is never any pressure to participate. Some members enjoy contributing to the discussion, while others prefer to listen and reflect. Both approaches are welcome.\n\nCamera on or camera off — the choice is yours. The goal is not simply to learn something new. It's to create meaningful change through consistent reflection and intentional action.",
    personalHeading: "A personal welcome",
    personalBody:
      "Thank you again for choosing to invest in yourself. I know there are many ways you could spend your time, attention, and resources, and I'm honored you've chosen to spend some of them with me and this community. I look forward to meeting you at your first Weekly Quantum Edge session.",
    signoff: "— Dan Hoover · Quantum Results",
  },
};
